import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {useForm, FormProvider} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";

// Components
import Modal from "../../components/Modal.js";
import {InputCheckGroup, InputSelect} from "../../components/form/FormInputs.js";

// Utils
import {getActiveChildIds, getPlacementOptions, isActiveBuilderElement} from "./helpers.js";

// Style
import {pad} from "../../style/components/variables.js";
import {
  FormGroup,
  FormField,
  ButtonFull,
  Form,
  HeadingCenter
} from "../../style/components/general.js";

const ModalAddToBuilder = ({
  visible,
  setShowModal,
  builder,
  facility,
  saveBuilder,
  element,
  setRefreshMissing
}) => {
  const [componentsMissing, setComponentsMissing] = useState(false);

  const schema = componentsMissing
    ? yup.object().shape({
        include: yup
          .mixed()
          .not([false], `Please select at least one component to add to ${element.label}.`)
      })
    : yup.object().shape({
        after: yup.string().required(`Please select a group after which to place ${element.label}.`)
      });

  const form = useForm({
    defaultValues: componentsMissing ? {include: false} : {after: null},
    resolver: yupResolver(schema)
  });

  const {handleSubmit, reset} = form;

  useEffect(() => {
    if (element.componentsMissing && Object.keys(element.componentsMissing).length > 0)
      setComponentsMissing(true);
  }, [element]);

  /**
   * Copy facility structure into the checksheet builder. Prefer existing checksheet
   * field defs when restoring a soft-deleted default element; always force active
   * (`toggle: true`, `deleted: false`) so state variants can keep the group while
   * default remains tombstoned.
   */
  const deepCopyElementAndChildren = (name, facilityBuilder, updatedBuilder) => {
    const source = facilityBuilder.byId[name];
    if (!source || source.deleted) return;

    const activeChildren = getActiveChildIds(facilityBuilder.byId, source.children);
    const existing = updatedBuilder.byId[name];

    updatedBuilder.byId[name] = {
      ...(existing || source),
      label: source.label ?? existing?.label,
      element: source.element ?? existing?.element,
      name,
      parentName: source.parentName ?? existing?.parentName ?? "",
      children: [...activeChildren],
      toggle: true,
      deleted: false
    };

    activeChildren.forEach(childName => {
      deepCopyElementAndChildren(childName, facilityBuilder, updatedBuilder);
    });
  };

  const insertIntoAllIds = (updated, name, after) => {
    if (updated.allIds.includes(name)) return;
    const idx = after === "_TOP_" ? 0 : updated.allIds.indexOf(after) + 1;
    updated.allIds.splice(Math.max(0, idx), 0, name);
  };

  const handleAddElement = values => {
    const updated = {
      allIds: [...builder.allIds],
      byId: Object.fromEntries(
        Object.entries(builder.byId).map(([key, val]) => [
          key,
          {...val, children: val?.children ? [...val.children] : []}
        ])
      )
    };
    if (componentsMissing) {
      if (Array.isArray(values.include)) {
        values.include.forEach(id => {
          if (!(id in updated.byId) || !updated.byId[element.name].children.includes(id))
            updated.byId[element.name].children.push(id);
          deepCopyElementAndChildren(id, facility.builder, updated);
        });
      } else {
        if (
          !(values.include in updated.byId) ||
          !updated.byId[element.name].children.includes(values.include)
        )
          updated.byId[element.name].children.push(values.include);
        deepCopyElementAndChildren(values.include, facility.builder, updated);
      }
    } else {
      // Soft-deleted default groups already exist in byId via construct_builder merge;
      // still must land in allIds for newFromBuilder / persistence.
      insertIntoAllIds(updated, element.name, values.after);
      deepCopyElementAndChildren(element.name, facility.builder, updated);
    }
    // Versioned save — same path as other editor mutations (opens history note when valid).
    saveBuilder(updated, undefined, undefined, true);
    setRefreshMissing(true);
    setShowModal(false);
    reset();
  };

  const placementOptions = getPlacementOptions(builder, facility?.builder);

  const componentOptions = (element.componentsMissing || [])
    .filter(name => isActiveBuilderElement(facility?.builder?.byId, name))
    .map(name => ({
      value: name,
      label: facility.builder.byId[name].label
    }));

  return (
    <Modal testId="facility.addFromFacility" visible={visible} setVisible={setShowModal}>
      <ModalTitle>Add From Facility Builder</ModalTitle>
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(handleAddElement)} style={{marginTop: "30px"}}>
          {componentsMissing ? (
            <FormGroup>
              <FormField>
                <InputCheckGroup
                  name="include"
                  testId="addFromFB.include"
                  label={`Select components to add to ${element.label}.`}
                  options={componentOptions}
                />
              </FormField>
            </FormGroup>
          ) : (
            <FormGroup>
              <FormField>
                <InputSelect
                  name="after"
                  testId="addFromFB.after"
                  options={placementOptions}
                  placeholder="Select element..."
                  label={`Select element after which to place ${element.label}.`}
                />
              </FormField>
            </FormGroup>
          )}

          <Submit type="submit" data-testid="addFromFB.submit">
            {componentsMissing ? "Add Components" : "Add Group"}
          </Submit>
        </Form>
      </FormProvider>
    </Modal>
  );
};

ModalAddToBuilder.propTypes = {
  visible: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  builder: PropTypes.objectOf(PropTypes.any).isRequired,
  facility: PropTypes.objectOf(PropTypes.any).isRequired,
  element: PropTypes.objectOf(PropTypes.any),
  saveBuilder: PropTypes.func.isRequired,
  setRefreshMissing: PropTypes.func.isRequired
};

// Style Overrides
const ModalTitle = styled(HeadingCenter)`
  margin: ${pad}px 0;
  color: ${props => props.theme.field};
`;

const Submit = styled(ButtonFull)`
  margin-bottom: ${pad}px;
`;

export default ModalAddToBuilder;
